import React, { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FillPct, Loc } from "./types";
import { fillColor } from "./utils";

type Props = {
  polite: Map<string, Loc[]>;
  highlight: FillPct | null;
  resolveNivel: (l: Loc) => FillPct;
};

type Align = "center" | "left" | "right";

const bucket = (v: number): 0 | 25 | 50 | 75 | 100 =>
  v >= 100 ? 100 : v >= 75 ? 75 : v >= 50 ? 50 : v >= 25 ? 25 : 0;

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));
const nf = new Intl.NumberFormat("ro-RO", { maximumFractionDigits: 2 });

const KGrid: React.FC<Props> = ({ polite, highlight, resolveNivel }) => {
  const politeOrder = useMemo(() => {
    const arr: string[] = [];
    polite.forEach((_cells, pKey) => arr.push(pKey));
    arr.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return arr;
  }, [polite]);

  const celOrder = useMemo(() => {
    const set = new Set<string>();
    polite.forEach((cells) => cells.forEach((l) => set.add(l.celula)));
    const arr = Array.from(set);
    arr.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return arr;
  }, [polite]);

  const [tip, setTip] = useState<{
    text: string;
    x: number;
    y: number;
    align: Align;
  } | null>(null);
  const showTip = useCallback((text: string, el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const TIP_W = 260,
      M = 12;

    let x = r.left + r.width / 2;
    let align: Align = "center";
    if (x + TIP_W / 2 + M > vw) {
      x = r.right - M;
      align = "right";
    } else if (x - TIP_W / 2 - M < 0) {
      x = r.left + M;
      align = "left";
    } else {
      x = clamp(x, M + TIP_W / 2, vw - M - TIP_W / 2);
    }

    let y = r.top - 10;
    if (y < 10) y = Math.min(r.bottom + 10, vh - 10);
    setTip({ text, x, y, align });
  }, []);
  const hideTip = useCallback(() => setTip(null), []);

  const hiClass = highlight == null ? "" : `wm-hi-${highlight}`;

  // Keep the cell tree stable while a tooltip is shown or the highlight changes.
  const rows = useMemo(() => politeOrder.map((pKey) => {
    const cells = polite.get(pKey)!;
    const byCel = new Map(cells.map((l) => [l.celula, l]));

    return (
      <div key={pKey} className="wm-polita-row">
        <div className="wm-polita-rail-item">{pKey}</div>
        <div className="wm-cells">
          {celOrder.map((cKey) => {
            const loc = byCel.get(cKey);
            const isGhost = !loc;
            const val = loc ? resolveNivel(loc) : 0;
            const lvl = bucket(val);
            const text = loc ? `${loc.locatie} • ${nf.format(val)}%` : "";

            return (
              <div
                key={`${pKey}-${cKey}-${loc?.id ?? cKey}`}
                className={`wm-cell-wrap wm-lvl-${lvl} ${isGhost ? "wm-ghost" : ""}`}
                data-cell={loc?.locatie}
                onMouseEnter={
                  loc ? (e) => showTip(text, e.currentTarget) : undefined
                }
                onMouseLeave={loc ? hideTip : undefined}>
                <div className={`wm-cell ${isGhost ? "wm-cell--ghost" : ""}`}>
                  {loc && (
                    <div
                      className="wm-fill"
                      style={{ height: `${val}%`, background: fillColor(val) }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }), [polite, politeOrder, celOrder, resolveNivel, showTip, hideTip]);

  return (
    <div className={`wm-grid-col ${hiClass}`}>
      {rows}

      {tip
        ? (createPortal(
            <div
              className="wm-tip-fixed"
              style={{ left: tip.x, top: tip.y }}
              data-align={tip.align}>
              {tip.text}
            </div>,
            document.body,
          ) as unknown as React.ReactNode)
        : null}
    </div>
  );
};

export default React.memo(KGrid);
